import { getMetaOgTags } from "./metaOgTags";
import {
  DomainNameWithSubPathForLocale,
  GetDomainName,
} from "~/helpers/UrlHelper";
import convertCurrency from "~/lib/helpers/currencyConverter";
import { generateBreadcrumbSchema } from "~/lib/helpers/schemaHelper";
import { listingSlug, propertySlug } from "~/lib/helpers/slugHelper";
import { OFFICE_TYPES } from "~/components/blocks/Geosearch/constants";
import {
  resolveAreaListingPlaceForHeadline,
  resolveAreaListingPlaceForSubheadline,
} from "~/lib/helpers/StringHelper";
import {
  getMapTierStartingPrice,
  type GlobalPriceTier,
} from "~/lib/helpers/mapPinsHelper";

// ─────────────────────────────────────────────────────────────────────────────
// TYPE DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────

interface CountryMetaData {
  country: string;
  region?: string;
}

interface RegionData {
  region: string;
  country?: string;
  currency?: string;
  currencyIsoCode?: string;
}

interface GeoSearchMetaData {
  data?: PropertyListing[];
  autoReCalibratedData?: PropertyListing[];
  totalProperties?: number;
  totalListings?: number;
}

interface AreaSummary {
  name?: string;
  metaTitle?: string;
  metaDescription?: string;
  spaceTypeFormula?: string;
  /** API field; same labels as `spaceTypeFormula` (e.g. "Coworking Desk"). */
  spaceType?: string;
  country?: string;
  state?: string;
  city?: string;
  suburb?: string;
  imageUrl?: string;
  geolocationLat?: number;
  geolocationLong?: number;
  totalAvailableSpaces?: number;
  minWeeklyPrice?: number;
  maxWeeklyPrice?: number;
  currencyIsoCode?: string;
  deskMonthlyPrice?: number;
  maxDesks?: number;
  minDesks?: number;
  avgMonthlyCostPerWorkstation?: number;
  description?: string;
  averageRating?: number;
  reviewCount?: number;
  officeTypes?: string[];
}

interface PropertyFeature {
  featureName?: string;
  feature?: string;
  isAvailable: boolean;
}

interface TransportLocation {
  name?: string;
  travelTimeFromProperty?: number | string;
  distanceFromProperty?: number | string;
}

interface Transport {
  type?: string;
  locations?: TransportLocation[];
}

interface NearbyLocation {
  name?: string;
  travelTimeFromProperty?: number | string;
  distanceFromProperty?: number | string;
}

interface Nearby {
  type?: string;
  locations?: NearbyLocation[];
}

interface PropertyAsset {
  url?: string;
  image?: string;
  alt?: string;
  order?: number;
}

interface PropertyListingUnit {
  id?: string;
  sfid?: string;
  spaceTypeFormula?: string;
  noOfWS?: number;
  advertisedPriceMonthly?: number;
  advertisedPriceWeekly?: number;
  priceMonthly?: number;
  priceWeekly?: number;
  costPerWorkstation?: number;
  deskMonthlyPrice?: number;
  currencyIsoCode?: string;
  propertyId?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  geolocationLat?: number;
  geolocationLong?: number;
  assets?: PropertyAsset[];
  isFeatured?: boolean;
  incentive?: string | null;
  propertyVideoUrl?: string | null;
  isPrivateProvider?: boolean;
  closestBusStopName?: string;
  closestBusStopTravelTime?: number;
  closestTrainStationName?: string;
  closestTrainStationTravelTime?: number;
  nearby?: Nearby[];
  transport?: Transport[];
  availabilityDate?: string;
  portalSpaceAvailableOn?: string;
}

interface PropertyListing {
  id?: string;
  state_picklist__c?: string;
  buildingName?: string;
  name?: string;
  addressFormula?: string;
  suburb?: string;
  city?: string;
  state?: string;
  country?: string;
  geolocationLat?: number;
  geolocationLong?: number;
  assets?: PropertyAsset[];
  partnerLogo?: string;
  workspaceProviderName?: string;
  workspaceProviderSlug?: string;
  workspaceID?: string;
  propertyFeatures?: PropertyFeature[];
  transport?: Transport[];
  nearby?: Nearby[];
  closestTrainStationName?: string;
  closestTrainStationTravelTime?: number | string;
  closestBusStopName?: string;
  closestBusStopTravelTime?: number | string;
  avgMonthlyCostPerWorkstation?: number;
  costPerWorkstation?: number;
  minPrice?: number;
  maxPrice?: number;
  currencyIsoCode?: string;
  totalAvailableSpaces?: number;
  propertyTotalActiveListings?: number;
  minDesks?: number;
  maxDesks?: number;
  minAvailableDesks?: number;
  maxAvailableDesks?: number;
  noOfWS?: number;
  hideStreetNumber?: boolean;
  streetNumber?: string | null;
  streetName?: string;
  spaceTypeFormula?: string;
  mohLastModifiedDate?: string;
  lastmodifieddate?: string;
  createddate?: string;
  createdDate?: string;
  createdAt?: string;
  publishedDate?: string;
  publishDate?: string;
  firstPublishedDate?: string;
  description?: string;
  totalFloors?: number;
  buildingGrade?: string;
  averageRating?: number;
  reviewCount?: number;
  propertySlug?: string;
  url?: string;
  telephone?: string;
  openingHours?: string[];
  workspaceTypes?: string[];
  webPrimaryKeyword?: string;
  propertyListings?: PropertyListingUnit[];
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/** Decode common HTML entities (including double-encoded `&amp;#39;` from CMS). */
function decodeHtmlEntities(val: string): string {
  let s = val;
  for (let i = 0; i < 6 && s.includes("&"); i++) {
    s = s
      .replace(/&amp;#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
      .replace(/&amp;#x([0-9a-f]+);/gi, (_, h) =>
        String.fromCharCode(parseInt(h, 16)),
      )
      .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
      .replace(/&#x([0-9a-f]+);/gi, (_, h) =>
        String.fromCharCode(parseInt(h, 16)),
      )
      .replace(/&amp;/g, "&");
  }
  return s
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
}

const sanitize = (val: any): string =>
  decodeHtmlEntities(
    String(val ?? "")
      .replace(/<[^>]*>/g, "")
      .replace(/[\x00-\x1F\x7F]/g, " ")
      .replace(/\s+/g, " ")
      .trim(),
  );

/** Schema/JSON-LD copy — decode entities so `&amp;` never appears as literal text in structured data. */
function schemaText(val: any): string {
  let s = sanitize(val);
  if (!s) return s;
  for (let i = 0; i < 4 && /&(?:#|amp)/i.test(s); i++) {
    s = decodeHtmlEntities(s);
  }
  return s.replace(/&amp\b/gi, "&");
}

/** Human place label for schema (suburb/city), not the SEO keyword title. */
function getAreaPlaceLabel(areaSummary: AreaSummary): string {
  const fromFields =
    areaSummary.suburb ??
    areaSummary.city ??
    areaSummary.state ??
    areaSummary.country;
  if (fromFields) return sanitize(fromFields);
  const n = areaSummary.name ?? "";
  const m = n.match(
    /^(?:office\s+space|serviced\s+office|coworking|workspace)\s+in\s+(.+)$/i,
  );
  if (m?.[1]) return sanitize(m[1]);
  return sanitize(n);
}

/** e.g. "Perth, WA" for meta titles. */
function getAreaPlaceForMetaTitle(areaSummary: AreaSummary): string {
  const locality = sanitize(areaSummary.city || areaSummary.suburb);
  const state = sanitize(areaSummary.state);
  if (locality && state && locality.toLowerCase() !== state.toLowerCase()) {
    return `${locality}, ${state}`;
  }
  return (
    locality ||
    getAreaPlaceLabel(areaSummary) ||
    state ||
    ""
  );
}

function resolveAreaSpaceTypeFormula(areaSummary: AreaSummary): string | undefined {
  const raw = areaSummary.spaceTypeFormula || areaSummary.spaceType;
  return raw ? sanitize(raw) : undefined;
}

/** City/suburb without state — e.g. "Perth" for "across Perth and surrounds". */
function getAreaLocalityName(areaSummary: AreaSummary): string {
  return (
    sanitize(areaSummary.city || areaSummary.suburb) ||
    getAreaPlaceLabel(areaSummary) ||
    ""
  );
}

/** Stats from the same listing units that become nested `Offer` nodes (monthly price + desks). */
interface NestedOfferStats {
  prices: number[];
  deskCounts: number[];
  offerCount: number;
  coworkingOfferCount: number;
  nonCoworkingOfferCount: number;
}

function isCoworkingSpaceType(spaceType: string | undefined): boolean {
  if (!spaceType) return false;
  return /coworking|co-working|hot desk|hotdesk/i.test(spaceType);
}

function recordListingOfferStats(
  listing: PropertyListingUnit,
  prices: number[],
  deskCounts: number[],
  coworkingOfferCount: { value: number },
  nonCoworkingOfferCount: { value: number },
) {
  const price = getListingMonthlyPrice(listing);
  if (price == null) return;
  const desks = toPositiveNumber(listing.noOfWS) ?? 1;
  prices.push(price);
  deskCounts.push(desks);
  if (isCoworkingSpaceType(listing.spaceTypeFormula)) {
    coworkingOfferCount.value += 1;
  } else {
    nonCoworkingOfferCount.value += 1;
  }
}

/** Collect prices & workstation counts only where `getListingMonthlyPrice` yields a value (matches exported Offers). */
function collectNestedOfferStats(
  properties: PropertyListing[],
  schemaItemMode: SchemaItemMode = "property",
): NestedOfferStats {
  const prices: number[] = [];
  const deskCounts: number[] = [];
  const coworkingOfferCount = { value: 0 };
  const nonCoworkingOfferCount = { value: 0 };

  for (const p of properties) {
    const nested = p.propertyListings ?? [];
    if (nested.length > 0) {
      for (const listing of nested) {
        recordListingOfferStats(
          listing,
          prices,
          deskCounts,
          coworkingOfferCount,
          nonCoworkingOfferCount,
        );
      }
      continue;
    }

    if (schemaItemMode === "listing") {
      // Geo-search cards are flat listing units (no nested propertyListings).
      recordListingOfferStats(
        p as PropertyListingUnit,
        prices,
        deskCounts,
        coworkingOfferCount,
        nonCoworkingOfferCount,
      );
    }
  }

  return {
    prices,
    deskCounts,
    offerCount: prices.length,
    coworkingOfferCount: coworkingOfferCount.value,
    nonCoworkingOfferCount: nonCoworkingOfferCount.value,
  };
}

/** Normalize CMS copy so numeric claims match this JSON-LD (listings + nested offers). */
function alignAreaDescriptionWithSchema(
  description: string,
  listingCount: number,
  stats: NestedOfferStats,
  currency?: string,
): string {
  if (!description) return description;
  let s = description;
  const priced = stats.offerCount;
  const cw = stats.coworkingOfferCount;
  const nonCw = stats.nonCoworkingOfferCount;
  const monthlyMin =
    stats.prices.length > 0 ? Math.min(...stats.prices) : undefined;
  const monthlyMax =
    stats.prices.length > 0 ? Math.max(...stats.prices) : undefined;
  const avgDeskRate = averageDeskRateFromOffers(stats);
  const curr = currency ?? "AUD";
  const listingRangeText =
    monthlyMin != null && monthlyMax != null
      ? `${monthlyMin.toLocaleString("en-US")} to ${monthlyMax.toLocaleString("en-US")} ${curr}`
      : undefined;
  const deskRateText =
    avgDeskRate != null
      ? `${avgDeskRate.toLocaleString("en-US")} ${curr}`
      : undefined;

  s = s.replace(
    /\b\d+(?:,\d{3})?\+\s+premium office spaces\b/gi,
    `${listingCount} workspace listings on this page (${priced} priced premium office options)`,
  );
  s = s.replace(
    /\b\d+(?:,\d{3})?\+\s+office spaces\b/gi,
    `${listingCount} workspace listings on this page (${priced} priced office options)`,
  );
  s = s.replace(
    /\bWith a total of \d+(?:,\d{3})*\s+available spaces\b/gi,
    `This page lists ${listingCount} workspace locations with ${priced} priced space options`,
  );
  s = s.replace(
    /\b(?:a |the )?total of \d+(?:,\d{3})*\s+available spaces\b/gi,
    `${listingCount} workspace listings on this page (${priced} priced options)`,
  );
  s = s.replace(
    /\b\d+(?:,\d{3})*\s+total available spaces\b/gi,
    `${listingCount} listings (${priced} priced options)`,
  );
  s = s.replace(
    /\bthere are currently \d+(?:,\d{3})*\s+office spaces\b/gi,
    `there are ${listingCount} workspace listings on this page (${priced} priced options)`,
  );
  s = s.replace(
    /\bare currently \d+(?:,\d{3})*\s+office spaces available for rent\b/gi,
    `are currently ${priced} priced office options across ${listingCount} listings`,
  );
  s = s.replace(
    /\ba total of \d+(?:,\d{3})*\s+available spaces\b/gi,
    `${priced} priced space options across ${listingCount} listings`,
  );
  s = s.replace(
    /\b\d+(?:,\d{3})*\s+private spaces\b/gi,
    `${nonCw} private or non-coworking options (this page)`,
  );
  s = s.replace(
    /\b\d+(?:,\d{3})*\s+coworking spaces\b/gi,
    `${cw} coworking options (this page)`,
  );
  s = s.replace(
    /\b\d+(?:,\d{3})*\s+coworking\s+space(?:s)?\s+available\b/gi,
    `${cw} coworking options available on this page`,
  );
  if (listingRangeText) {
    s = s.replace(
      /\bprices?\s+range(?:s)?\s+from\s+[^\.,;]*?\bper\s+desk(?:\s*\/\s*(?:mo|month))?\b/gi,
      `listed monthly office totals range from ${listingRangeText}`,
    );
  }
  if (listingRangeText && deskRateText) {
    s = s.replace(
      /\bprices?\s+range(?:s)?\s+from\s+[^\.,;]*/gi,
      `listed monthly office totals range from ${listingRangeText}, with an indicative average desk rate of ${deskRateText} per desk/month`,
    );
  }
  return s;
}

const isBrokenPartnerLogo = (url?: string): boolean =>
  !url || url.includes("/null/") || url.includes("/Site/Logo/null/");

function deepSanitizeSchema(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === "string") return schemaText(obj);
  if (typeof obj === "number" || typeof obj === "boolean") return obj;
  if (Array.isArray(obj)) return obj.map(deepSanitizeSchema);
  if (typeof obj === "object") {
    const result: any = {};
    for (const key of Object.keys(obj)) {
      result[key] = deepSanitizeSchema(obj[key]);
    }
    return result;
  }
  return obj;
}

interface PriceRange {
  min: number;
  max: number;
  avg: number;
}

const toPositiveNumber = (val: unknown): number | undefined => {
  if (val == null) return undefined;
  const num = typeof val === "string" ? Number(val) : val;
  return typeof num === "number" && Number.isFinite(num) && num > 0
    ? num
    : undefined;
};

const getListingMonthlyPrice = (
  listing: PropertyListingUnit,
): number | undefined =>
  toPositiveNumber(listing.advertisedPriceMonthly) ??
  toPositiveNumber(listing.priceMonthly) ??
  toPositiveNumber(listing.costPerWorkstation) ??
  toPositiveNumber(listing.deskMonthlyPrice);

/** Property pages quote a per-desk monthly rate; listing pages quote the whole-space total. */
function getSchemaOfferPrice(
  listing: PropertyListingUnit,
  schemaItemMode: SchemaItemMode,
): { price: number; referenceDesks: number } | undefined {
  const desks = toPositiveNumber(listing.noOfWS) ?? 1;
  if (schemaItemMode === "property") {
    const perDesk =
      toPositiveNumber(listing.costPerWorkstation) ??
      toPositiveNumber(listing.deskMonthlyPrice);
    if (perDesk != null) return { price: perDesk, referenceDesks: 1 };
    const spaceTotal = getListingMonthlyPrice(listing);
    if (spaceTotal == null) return undefined;
    return { price: Math.round(spaceTotal / desks), referenceDesks: 1 };
  }

  const price = getListingMonthlyPrice(listing);
  if (price == null) return undefined;
  return { price, referenceDesks: desks };
}

const getPriceRange = (prices: number[]): PriceRange | undefined => {
  if (prices.length === 0) return undefined;
  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
    avg: Math.round(
      prices.reduce((total, price) => total + price, 0) / prices.length,
    ),
  };
};

const getPropertyMonthlyPrices = (property: PropertyListing): number[] => {
  const nestedPrices = (property.propertyListings ?? [])
    .map(getListingMonthlyPrice)
    .filter((price): price is number => price != null);

  if (nestedPrices.length > 0) {
    const propertyLevelPrices = [
      toPositiveNumber(property.costPerWorkstation),
      toPositiveNumber(property.avgMonthlyCostPerWorkstation),
    ].filter((price): price is number => price != null);
    return [...nestedPrices, ...propertyLevelPrices];
  }

  // Geo-search cards are flat listing units (advertisedPriceMonthly, etc.)
  const flatPrices = [
    getListingMonthlyPrice(property as PropertyListingUnit),
    toPositiveNumber(property.costPerWorkstation),
    toPositiveNumber(property.avgMonthlyCostPerWorkstation),
    toPositiveNumber(
      (property as PropertyListing & { priceMonthlyMin?: number })
        .priceMonthlyMin,
    ),
  ].filter((price): price is number => price != null);

  return flatPrices;
};

const getPropertyMonthlyPriceRange = (
  property: PropertyListing,
): PriceRange | undefined => getPriceRange(getPropertyMonthlyPrices(property));

/** Min/max desks across buildings using availability fields (falls back to min/max desks). */
function getAggregatedDeskCapacityFromProperties(
  properties: PropertyListing[],
): { min: number; max: number } | undefined {
  const lows: number[] = [];
  const highs: number[] = [];
  for (const p of properties) {
    const lo = toPositiveNumber(p.minAvailableDesks ?? p.minDesks);
    const hi = toPositiveNumber(p.maxAvailableDesks ?? p.maxDesks);
    if (lo != null) lows.push(lo);
    if (hi != null) highs.push(hi);
  }
  if (lows.length === 0 && highs.length === 0) return undefined;
  const min = lows.length > 0 ? Math.min(...lows) : Math.min(...highs);
  const max = highs.length > 0 ? Math.max(...highs) : Math.max(...lows);
  return { min, max };
}

function formatDeskCapacityRange(cap: { min: number; max: number }): string {
  if (cap.min === cap.max) return `${cap.min} Desks`;
  return `${cap.min} to ${cap.max} Desks`;
}

/** Min/max desks from geosearch payload only (properties + nested listings). */
function getDeskCapacityRangeFromListingPayload(
  listings: PropertyListing[],
): { min: number; max: number } | undefined {
  if (!listings.length) return undefined;

  const fromProperties = getAggregatedDeskCapacityFromProperties(listings);
  if (fromProperties) return fromProperties;

  const stats = collectNestedOfferStats(listings);
  if (stats.deskCounts.length > 0) {
    return {
      min: Math.min(...stats.deskCounts),
      max: Math.max(...stats.deskCounts),
    };
  }

  return undefined;
}

/** e.g. " for 1–50+ people" — used in Private Office meta descriptions. */
function formatMetaDescriptionPeopleCapacity(cap: {
  min: number;
  max: number;
}): string {
  const lo = Math.max(1, cap.min);
  const hi = Math.max(lo, cap.max);
  if (lo === hi) return ` for ${lo} people`;
  return ` for ${lo}–${hi}+ people`;
}

type SchemaItemMode = "property" | "listing";

const buildPropertyUrl = (
  siteOrigin: string,
  property: PropertyListing,
): string | undefined => {
  const origin = siteOrigin.replace(/\/$/, "");
  if (property.url) {
    const u = property.url.trim();
    if (u.startsWith("http://") || u.startsWith("https://")) return u;
    return `${origin}${u.startsWith("/") ? u : `/${u}`}`;
  }
  if (property.propertySlug)
    return `${origin}/office-space/${property.propertySlug}`;
  if (property.id) {
    const path = propertySlug(property as Parameters<typeof propertySlug>[0]);
    if (path) return `${origin}${path}`;
  }
  return undefined;
};

const buildListingItemUrl = (
  siteOrigin: string,
  listing: PropertyListing,
): string | undefined => {
  const origin = siteOrigin.replace(/\/$/, "");
  const path = listingSlug(listing as Parameters<typeof listingSlug>[0]);
  if (!path) return undefined;
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
};

const dedupeAmenities = (amenities: any[]): any[] => {
  const seen = new Set<string>();
  return amenities.filter((a) => {
    const key = `${a.name}|${a.additionalType ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

function toValidSchemaDate(val: unknown): string | undefined {
  if (typeof val !== "string") return undefined;
  const v = val.trim();
  if (!v) return undefined;
  const ts = Date.parse(v);
  if (Number.isNaN(ts)) return undefined;
  return new Date(ts).toISOString();
}

function getOriginalPublishDate(property: PropertyListing): string | undefined {
  return (
    toValidSchemaDate(property.firstPublishedDate) ??
    toValidSchemaDate(property.publishDate) ??
    toValidSchemaDate(property.publishedDate) ??
    toValidSchemaDate(property.createdDate) ??
    toValidSchemaDate(property.createdAt) ??
    toValidSchemaDate(property.createddate)
  );
}

function getAvailabilityStarts(
  listing: PropertyListingUnit,
): string | undefined {
  return (
    toValidSchemaDate(listing.portalSpaceAvailableOn) ??
    toValidSchemaDate(listing.availabilityDate)
  );
}

/** Single segment for URL fragment @id (Salesforce ids, etc.). */
function schemaIdToken(
  raw: string | number | undefined,
  fallback: string,
): string {
  const s = String(raw ?? "").trim();
  const base = s.length > 0 ? s : fallback;
  return base.replace(/[^a-zA-Z0-9._-]+/g, "-");
}

function stablePropertySchemaKey(
  property: PropertyListing,
  position: number,
): string {
  return schemaIdToken(
    property.id ?? property.workspaceID,
    `position-${position}`,
  );
}

function stableUnitSchemaKey(unit: PropertyListingUnit, index: number): string {
  return schemaIdToken(
    unit.id ?? unit.sfid ?? unit.propertyId,
    `unit-${index}`,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AMENITY BUILDER
// ─────────────────────────────────────────────────────────────────────────────

function buildAmenityFeatures(property: PropertyListing): any[] {
  const amenities: any[] = [];

  if (Array.isArray(property.propertyFeatures)) {
    property.propertyFeatures
      .filter((f) => f.isAvailable)
      .forEach((f) => {
        const name = sanitize(f.featureName ?? f.feature);
        if (name) {
          amenities.push({
            "@type": "LocationFeatureSpecification",
            name,
            value: true,
          });
        }
      });
  }

  if (Array.isArray(property.transport)) {
    property.transport.forEach((t) => {
      (t.locations ?? []).forEach((loc) => {
        const name = sanitize(loc.name);
        if (!name) return;
        const isAirport = t.type === "airport";
        amenities.push({
          "@type": "LocationFeatureSpecification",
          name,
          additionalType: t.type ?? "",
          description: isAirport
            ? sanitize(String(loc.distanceFromProperty ?? ""))
            : `${loc.travelTimeFromProperty ?? ""} minute walk`,
        });
      });
    });
  }

  if (property.closestTrainStationName) {
    amenities.push({
      "@type": "LocationFeatureSpecification",
      name: sanitize(property.closestTrainStationName),
      additionalType: "train",
      description: `${property.closestTrainStationTravelTime ?? ""} minute walk`,
    });
  }
  if (property.closestBusStopName) {
    amenities.push({
      "@type": "LocationFeatureSpecification",
      name: sanitize(property.closestBusStopName),
      additionalType: "bus",
      description: `${property.closestBusStopTravelTime ?? ""} minute walk`,
    });
  }

  if (Array.isArray(property.nearby)) {
    property.nearby.forEach((n) => {
      (n.locations ?? []).forEach((loc) => {
        const name = sanitize(loc.name);
        if (!name) return;
        amenities.push({
          "@type": "LocationFeatureSpecification",
          name,
          additionalType: n.type ?? "",
          description: sanitize(
            String(
              loc.travelTimeFromProperty ?? loc.distanceFromProperty ?? "",
            ),
          ),
        });
      });
    });
  }

  return dedupeAmenities(amenities);
}

// ─────────────────────────────────────────────────────────────────────────────
// INDIVIDUAL LISTING OFFERS BUILDER
// ─────────────────────────────────────────────────────────────────────────────

function buildListingOffers(
  propertyListings: PropertyListingUnit[],
  fallbackCurrency: string,
  pageUrl: string,
  propertyKey: string,
  schemaItemMode: SchemaItemMode = "property",
): any[] {
  return propertyListings.reduce<any[]>((offers, listing) => {
    const priced = getSchemaOfferPrice(listing, schemaItemMode);
    if (!priced) return offers;
    const { price, referenceDesks } = priced;
    const currency = listing.currencyIsoCode ?? fallbackCurrency;
    const spaceType = listing.spaceTypeFormula ?? "Office";
    const desks = listing.noOfWS ?? 1;
    const offerName = `${spaceType} Office — ${desks} ${desks === 1 ? "Desk" : "Desks"}`;
    const offerIx = offers.length;
    const unitKey = stableUnitSchemaKey(listing, offerIx);
    const offerId = `${pageUrl}#offer:${propertyKey}:${unitKey}`;
    const availabilityStarts = getAvailabilityStarts(listing);
    const isListingMode = schemaItemMode === "listing";

    const listingImage =
      Array.isArray(listing.assets) && listing.assets.length > 0
        ? (listing.assets[0].url ?? listing.assets[0].image)
        : undefined;

    offers.push({
      "@type": "Offer",
      "@id": offerId,
      name: offerName,
      ...(listingImage && { image: listingImage }),
      price,
      priceCurrency: currency,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        "@id": `${offerId}/price-spec`,
        price,
        priceCurrency: currency,
        unitText: "MONTH",
        referenceQuantity: {
          "@type": "QuantitativeValue",
          value: referenceDesks,
          unitText: "workstation",
        },
      },
      additionalType: spaceType,
      availability: "https://schema.org/InStock",
      ...(availabilityStarts && { availabilityStarts }),
      description: isListingMode
        ? `Monthly office total for this listed unit (${desks} ${desks === 1 ? "desk" : "desks"}). This is not a per-desk monthly rate.`
        : "Monthly per-desk rate for this property.",
      ...(listing.incentive && {
        additionalProperty: [
          {
            "@type": "PropertyValue",
            name: "Incentive",
            value: sanitize(listing.incentive),
          },
        ],
      }),
    });

    return offers;
  }, []);
}

function buildPropertyListItem(
  property: PropertyListing,
  position: number,
  baseUrl: string,
  siteOrigin: string,
  pageUrl: string,
  schemaItemMode: SchemaItemMode = "property",
): any {
  const propertyKey = stablePropertySchemaKey(property, position);
  const realEstateListingId = `${pageUrl}#real-estate-listing:${propertyKey}`;
  const images = (property.assets ?? [])
    .slice(0, 7)
    .map((a) => a.url ?? a.image)
    .filter(Boolean) as string[];

  const amenities = buildAmenityFeatures(property);

  const currency =
    property.currencyIsoCode ??
    property.propertyListings?.find((listing) => listing.currencyIsoCode)
      ?.currencyIsoCode ??
    "AUD";

  const nestedListingUnits = property.propertyListings ?? [];
  const listingUnits =
    nestedListingUnits.length > 0
      ? nestedListingUnits
      : schemaItemMode === "listing"
        ? [property as PropertyListingUnit]
        : [];
  const individualOffers = buildListingOffers(
    listingUnits,
    currency,
    pageUrl,
    propertyKey,
    schemaItemMode,
  );

  let minCost = 0;
  let maxCost = 0;
  let avgCost = 0;
  let hasPriceRange = false;

  if (individualOffers.length > 0) {
    const offerPrices = individualOffers
      .map((o: { price?: number }) => o.price)
      .filter((p): p is number => typeof p === "number" && p > 0);
    if (offerPrices.length > 0) {
      minCost = Math.min(...offerPrices);
      maxCost = Math.max(...offerPrices);
      avgCost = Math.round(
        offerPrices.reduce((total, price) => total + price, 0) /
        offerPrices.length,
      );
      hasPriceRange = minCost > 0 && maxCost > 0 && minCost !== maxCost;
    }
  } else {
    const propertyPriceRange = getPropertyMonthlyPriceRange(property);
    minCost = propertyPriceRange?.min ?? 0;
    maxCost = propertyPriceRange?.max ?? 0;
    avgCost = propertyPriceRange?.avg ?? 0;
    hasPriceRange = minCost > 0 && maxCost > 0 && minCost !== maxCost;
  }

  const spaceTypesPresent = [
    ...new Set(listingUnits.map((l) => l.spaceTypeFormula).filter(Boolean)),
  ] as string[];

  const buildingName = sanitize(
    property.buildingName ?? property.name ?? property.addressFormula,
  );
  const providerName = sanitize(property.workspaceProviderName);
  const itemUrl =
    schemaItemMode === "listing"
      ? buildListingItemUrl(siteOrigin, property)
      : buildPropertyUrl(siteOrigin, property);

  const desksFromOffers = individualOffers
    .map(
      (o: {
        priceSpecification?: { referenceQuantity?: { value?: number } };
      }) => toPositiveNumber(o.priceSpecification?.referenceQuantity?.value),
    )
    .filter((n): n is number => n != null);
  const deskLo =
    desksFromOffers.length > 0
      ? Math.min(...desksFromOffers)
      : (toPositiveNumber(property.minAvailableDesks) ??
        toPositiveNumber(property.minDesks) ??
        (schemaItemMode === "listing"
          ? toPositiveNumber(property.noOfWS)
          : undefined) ??
        1);
  const deskHiFromOffers =
    desksFromOffers.length > 0 ? Math.max(...desksFromOffers) : undefined;
  const deskHiField =
    toPositiveNumber(property.maxAvailableDesks) ??
    toPositiveNumber(property.maxDesks);
  const deskHi = deskHiFromOffers ?? deskHiField;
  const deskRange =
    deskHi != null && deskLo !== deskHi
      ? `${deskLo} to ${deskHi}`
      : `${deskLo}`;

  const availableSpacesCount = (() => {
    const raw =
      property.totalAvailableSpaces ??
      property.propertyTotalActiveListings ??
      0;
    if (raw > 0) return raw;
    return individualOffers.length > 0 ? individualOffers.length : 0;
  })();

  const buildingAdditionalProps: any[] = [
    ...(property.buildingName
      ? [
        {
          "@type": "PropertyValue",
          name: "Building Name",
          value: buildingName,
        },
      ]
      : []),
    ...(availableSpacesCount > 0
      ? [
        {
          "@type": "PropertyValue",
          name: "Available Spaces",
          value: availableSpacesCount,
        },
      ]
      : []),
    {
      "@type": "PropertyValue",
      name: "Desk Range",
      value: deskRange,
    },
    ...(property.buildingGrade
      ? [
        {
          "@type": "PropertyValue",
          name: "Building Grade",
          value: sanitize(property.buildingGrade),
        },
      ]
      : []),
    ...(property.totalFloors
      ? [
        {
          "@type": "PropertyValue",
          name: "Total Floors",
          value: property.totalFloors,
        },
      ]
      : []),
    ...(spaceTypesPresent.length > 0
      ? [
        {
          "@type": "PropertyValue",
          name: "Workspace Types",
          value: spaceTypesPresent.join(", "),
        },
      ]
      : Array.isArray(property.workspaceTypes) &&
        property.workspaceTypes.length > 0
        ? [
          {
            "@type": "PropertyValue",
            name: "Workspace Types",
            value: property.workspaceTypes.join(", "),
          },
        ]
        : []),
    ...(property.webPrimaryKeyword
      ? [
        {
          "@type": "PropertyValue",
          name: "Primary Keyword",
          value: sanitize(property.webPrimaryKeyword),
        },
      ]
      : []),
  ];

  const offerNode: any | undefined = (() => {
    if (minCost <= 0 && individualOffers.length === 0) return undefined;

    if (individualOffers.length > 0) {
      const aggregateId = `${pageUrl}#aggregate-offer:${propertyKey}`;
      return {
        "@type": "AggregateOffer",
        "@id": aggregateId,
        name: `Office Space at ${buildingName}`,
        ...(hasPriceRange
          ? { lowPrice: minCost, highPrice: maxCost }
          : { lowPrice: avgCost, highPrice: avgCost }),
        priceCurrency: currency,
        offerCount: individualOffers.length,
        availability: "https://schema.org/InStock",
        description:
          schemaItemMode === "listing"
            ? "Price range reflects monthly office totals across listed units, not per-desk monthly rates."
            : "Price range reflects monthly per-desk rates across this property.",
        ...(itemUrl && { url: itemUrl }),
        offers: individualOffers,
      };
    }

    const summaryOfferId = hasPriceRange
      ? `${pageUrl}#aggregate-offer:${propertyKey}:rollup`
      : `${pageUrl}#offer:${propertyKey}:summary`;
    return {
      "@type": hasPriceRange ? "AggregateOffer" : "Offer",
      "@id": summaryOfferId,
      name: `Office Space at ${buildingName}`,
      ...(hasPriceRange && { lowPrice: minCost, highPrice: maxCost }),
      price: avgCost,
      priceCurrency: currency,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        "@id": `${summaryOfferId}/price-spec`,
        price: avgCost,
        priceCurrency: currency,
        unitText: "MONTH",
        referenceQuantity: {
          "@type": "QuantitativeValue",
          value: 1,
          unitText: "workstation",
        },
      },
      availability: "https://schema.org/InStock",
      description:
        schemaItemMode === "listing"
          ? "Price reflects a monthly office total for the listing summary, not a per-desk monthly rate."
          : "Price reflects a monthly per-desk rate for this property.",
      ...(itemUrl && { url: itemUrl }),
    };
  })();

  const spaceTypesString =
    spaceTypesPresent.length > 0
      ? spaceTypesPresent.join(", ")
      : "Office Space";

  const listingName = `${spaceTypesString} for Lease at ${buildingName}`;

  const officeBuildingNode: any = {
    "@type": "LocalBusiness",
    name: buildingName,
    ...(property.description && {
      description: sanitize(property.description),
    }),
    address: {
      "@type": "PostalAddress",
      streetAddress: sanitize(property.addressFormula ?? property.name),
      addressLocality: sanitize(property.suburb ?? property.city),
      addressRegion: sanitize(property.state),
      addressCountry: sanitize(property.country),
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: property.geolocationLat,
      longitude: property.geolocationLong,
    },
    ...(images.length > 0 && { image: images }),
    ...(property.partnerLogo &&
      !isBrokenPartnerLogo(property.partnerLogo) && {
      logo: property.partnerLogo,
    }),
    ...(property.telephone && { telephone: property.telephone }),
    ...(Array.isArray(property.openingHours) &&
      property.openingHours.length > 0 && {
      openingHours: property.openingHours,
    }),
    ...(itemUrl && { url: itemUrl }),
    priceRange: hasPriceRange
      ? schemaItemMode === "listing"
        ? `${minCost} - ${maxCost} ${currency} (monthly office totals)`
        : `${minCost} - ${maxCost} ${currency} (monthly per-desk rates)`
      : schemaItemMode === "listing"
        ? `${avgCost} ${currency} (monthly office total)`
        : `${avgCost} ${currency} (monthly per-desk rate)`,
    additionalProperty: buildingAdditionalProps,
    ...(amenities.length > 0 && { amenityFeature: amenities }),
    ...(property.averageRating && property.reviewCount
      ? {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: property.averageRating,
          reviewCount: property.reviewCount,
          bestRating: 5,
          worstRating: 1,
        },
      }
      : {}),
    ...(providerName && {
      brand: {
        "@type": "Brand",
        name: providerName,
        ...(property.workspaceProviderSlug && {
          url: `${baseUrl}/coworking/${property.workspaceProviderSlug}`,
        }),
      },
    }),
  };
  const publishedDate = getOriginalPublishDate(property);

  const realEstateListing: any = {
    "@type": "RealEstateListing",
    "@id": realEstateListingId,
    name: listingName,
    ...(images.length > 0 && { image: images[0] }),
    ...(publishedDate && { datePosted: publishedDate }),
    ...(itemUrl && { url: itemUrl }),
    ...(offerNode && { offers: offerNode }),
    about: officeBuildingNode,
  };

  return {
    "@type": "ListItem",
    position,
    item: realEstateListing,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// PLACE / AREA SCHEMA
// ─────────────────────────────────────────────────────────────────────────────

/** Mean of (monthly total / workstations) across nested offers — aligns with Offer.price. */
function averageDeskRateFromOffers(
  stats: NestedOfferStats,
): number | undefined {
  if (stats.prices.length === 0) return undefined;
  let sum = 0;
  for (let i = 0; i < stats.prices.length; i++) {
    const d = Math.max(1, stats.deskCounts[i] ?? 1);
    sum += stats.prices[i] / d;
  }
  return Math.round(sum / stats.prices.length);
}

function buildPlaceSchema(
  areaSummary: AreaSummary,
  properties: PropertyListing[],
  pageUrl: string,
  listingCount: number,
  placeLabel: string,
  offerStats: NestedOfferStats,
  metaDescription?: string,
): any {
  const currency =
    areaSummary.currencyIsoCode ??
    properties.find((property) => property.currencyIsoCode)?.currencyIsoCode ??
    "AUD";

  let monthlyMin: number | null = null;
  let monthlyMax: number | null = null;
  if (offerStats.prices.length > 0) {
    monthlyMin = Math.min(...offerStats.prices);
    monthlyMax = Math.max(...offerStats.prices);
  }

  let capacityStr: string;
  if (offerStats.deskCounts.length > 0) {
    const lo = Math.min(...offerStats.deskCounts);
    const hi = Math.max(...offerStats.deskCounts);
    capacityStr = formatDeskCapacityRange({ min: lo, max: hi });
  } else {
    const deskCap = getAggregatedDeskCapacityFromProperties(properties);
    if (deskCap) {
      capacityStr = formatDeskCapacityRange(deskCap);
    } else {
      const lo = toPositiveNumber(areaSummary.minDesks) ?? 1;
      const hi = toPositiveNumber(areaSummary.maxDesks) ?? lo;
      capacityStr = formatDeskCapacityRange({ min: lo, max: hi });
    }
  }

  const avgDeskFromOffers = averageDeskRateFromOffers(offerStats);

  const hasSpaces =
    listingCount > 0 ||
    offerStats.offerCount > 0 ||
    (areaSummary.totalAvailableSpaces ?? 0) > 0;

  const additionalProperty: any[] = hasSpaces
    ? [
      ...(monthlyMin != null &&
        monthlyMax != null &&
        monthlyMin > 0 &&
        monthlyMax > 0
        ? [
          {
            "@type": "PropertyValue",
            name: "Monthly price range (listing totals)",
            value:
              monthlyMin === monthlyMax
                ? `${monthlyMin} ${currency}`
                : `${monthlyMin} to ${monthlyMax} ${currency}`,
          },
        ]
        : []),
      {
        "@type": "PropertyValue",
        name: "Workspace listings (this page)",
        value: String(listingCount),
      },
      ...(offerStats.offerCount > 0
        ? [
          {
            "@type": "PropertyValue",
            name: "Priced space options (this page)",
            value: String(offerStats.offerCount),
          },
        ]
        : []),
      {
        "@type": "PropertyValue",
        name: "Capacity (workstations in priced offers)",
        value: capacityStr,
      },
      ...(avgDeskFromOffers != null
        ? [
          {
            "@type": "PropertyValue",
            name: "Average indicative desk rate (Month)",
            value: `${avgDeskFromOffers} ${currency}`,
          },
        ]
        : areaSummary.deskMonthlyPrice
          ? [
            {
              "@type": "PropertyValue",
              name: "AVG Desk Price (Month)",
              value: `${areaSummary.deskMonthlyPrice} ${currency}`,
            },
          ]
          : []),
      ...(Array.isArray(areaSummary.officeTypes) &&
        areaSummary.officeTypes.length > 0
        ? [
          {
            "@type": "PropertyValue",
            name: "Office Types Available",
            value: areaSummary.officeTypes.join(", "),
          },
        ]
        : []),
    ]
    : [];

  const descriptionRaw = metaDescription
    ? schemaText(metaDescription)
    : areaSummary.description
      ? schemaText(
        alignAreaDescriptionWithSchema(
          sanitize(areaSummary.description),
          listingCount,
          offerStats,
          currency,
        ),
      )
      : "";

  return {
    "@type": "Place",
    "@id": `${pageUrl}#place`,
    name: placeLabel,
    url: pageUrl,
    ...(descriptionRaw && {
      description: descriptionRaw,
    }),
    address: {
      "@type": "PostalAddress",
      addressCountry: sanitize(areaSummary.country),
      addressRegion: sanitize(areaSummary.state),
      addressLocality: placeLabel,
    },
    ...(areaSummary.geolocationLat &&
      areaSummary.geolocationLong && {
      geo: {
        "@type": "GeoCoordinates",
        latitude: areaSummary.geolocationLat,
        longitude: areaSummary.geolocationLong,
      },
    }),
    ...(areaSummary.imageUrl && {
      image: `https://ik.imagekit.io/officehub/tr:w-2400,h-1256,c-maintain_ratio,f-auto/${areaSummary.imageUrl}`,
    }),
    ...(areaSummary.averageRating && areaSummary.reviewCount
      ? {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: areaSummary.averageRating,
          reviewCount: areaSummary.reviewCount,
          bestRating: 5,
          worstRating: 1,
        },
      }
      : {}),
    ...(additionalProperty.length > 0 && { additionalProperty }),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// COLLECTION PAGE (ROOT SCHEMA)
// ─────────────────────────────────────────────────────────────────────────────

/** ItemList name/description — same office-type phrasing as the listing H1. */
function getCollectionItemListLabel(
  areaSummary: AreaSummary,
  placeLabel: string,
): string {
  const spaceType = normalizeSpaceTypeForAreaPageTemplate(
    resolveAreaSpaceTypeFormula(areaSummary),
  );
  if (!spaceType) return `Office Spaces in ${placeLabel}`;
  return getAreaMetaTitleTemplate(spaceType).headline(placeLabel);
}

function buildCollectionPageSchema(
  areaSummary: AreaSummary,
  properties: PropertyListing[],
  pageUrl: string,
  baseUrl: string,
  siteOrigin: string,
  title: string,
  description: string,
  totalListingCount: number,
  schemaItemMode: SchemaItemMode = "property",
): any {
  const placeLabel = getAreaPlaceLabel(areaSummary);
  const listingCount = properties.length;
  const totalCount =
    totalListingCount > 0 ? totalListingCount : listingCount;
  const itemListLabel = getCollectionItemListLabel(areaSummary, placeLabel);
  const offerStats = collectNestedOfferStats(properties, schemaItemMode);

  const itemListElement = properties.map((property, index) =>
    buildPropertyListItem(
      property,
      index + 1,
      baseUrl,
      siteOrigin,
      pageUrl,
      schemaItemMode,
    ),
  );

  const placeNode = buildPlaceSchema(
    areaSummary,
    properties,
    pageUrl,
    listingCount,
    placeLabel,
    offerStats,
    description,
  );

  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${pageUrl}#webpage`,
    name: schemaText(title ?? areaSummary.name),
    // Same copy as og:description / meta name="description" (not CMS areaSummary.description).
    description: schemaText(description),
    url: pageUrl,
    about: placeNode,
    ...(itemListElement.length > 0 && {
      mainEntity: {
        "@type": "ItemList",
        name: itemListLabel,
        description: `${totalCount.toLocaleString()}${totalCount > 0 ? "+" : ""} ${itemListLabel}`,
        numberOfItems: totalCount,
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        itemListElement,
      },
    }),
  };
}

function areaOfficeTypeLabel(spaceTypeFormula?: string): string {
  if (!spaceTypeFormula) return "Office Space";
  const match = OFFICE_TYPES.find(
    (t) => t.label.toLowerCase() === spaceTypeFormula.toLowerCase(),
  );
  return match?.label ?? spaceTypeFormula;
}

function buildAreaBreadcrumbSchema(
  areaSummary: AreaSummary,
  pageUrl: string,
  siteOrigin: string,
) {
  const crumbs: { name: string; path: string | null }[] = [
    { name: "Home", path: "/" },
  ];
  if (areaSummary.country) {
    crumbs.push({ name: String(areaSummary.country), path: null });
  }
  if (areaSummary.state) {
    crumbs.push({ name: String(areaSummary.state), path: null });
  }
  const city = areaSummary.city || areaSummary.suburb;
  if (city) {
    crumbs.push({ name: String(city), path: null });
  }
  crumbs.push({
    name: areaOfficeTypeLabel(areaSummary.spaceTypeFormula),
    path: null,
  });
  return generateBreadcrumbSchema(crumbs, pageUrl, siteOrigin);
}

type AreaMetaTitlePriceUnit = "desk" | "monthly";

interface AreaMetaTitleTemplate {
  headline: (place: string) => string;
  priceSegment: (priceWithUnit: string | undefined) => string;
  priceUnit: AreaMetaTitlePriceUnit;
  includePrice: boolean;
}

const AREA_PAGE_META_TITLE_BY_SPACE_TYPE: Record<string, AreaMetaTitleTemplate> =
{
  "Serviced Office": {
    headline: (place) =>
      place ? `Serviced Offices in ${place}` : "Serviced Offices",
    priceSegment: (price) =>
      price ? `All-Inclusive from ${price}` : "All-Inclusive",
    priceUnit: "desk",
    includePrice: true,
  },
  "Private Office": {
    headline: (place) =>
      place
        ? `Private Offices for Rent in ${place}`
        : "Private Offices for Rent",
    priceSegment: (price) => (price ? `From ${price}` : "For Rent"),
    priceUnit: "desk",
    includePrice: true,
  },
  "Coworking Space": {
    headline: (place) =>
      place ? `Coworking Space in ${place}` : "Coworking Space",
    priceSegment: (price) =>
      price ? `Flexible Desks from ${price}` : "Flexible Desks",
    priceUnit: "monthly",
    includePrice: true,
  },
  "Sublet Office": {
    headline: (place) =>
      place ? `Sublease Office Space in ${place}` : "Sublease Office Space",
    priceSegment: (price) =>
      price ? `Short-Term Rentals from ${price}` : "Short-Term Rentals",
    priceUnit: "monthly",
    includePrice: true,
  },
  "Virtual Office": {
    headline: (place) =>
      place ? `Virtual Office in ${place}` : "Virtual Office",
    priceSegment: (price) =>
      price ? `Business Address from ${price}` : "Business Address",
    priceUnit: "monthly",
    includePrice: true,
  },
  "Enterprise Office": {
    headline: (place) =>
      place
        ? `Enterprise Office Space in ${place}`
        : "Enterprise Office Space",
    priceSegment: () => "Scalable Workspaces",
    priceUnit: "desk",
    includePrice: false,
  },
  "Shared Office": {
    headline: (place) =>
      place ? `Shared Office Space in ${place}` : "Shared Office Space",
    priceSegment: (price) =>
      price ? `Hot Desks from ${price}` : "Hot Desks",
    priceUnit: "monthly",
    includePrice: true,
  },
  "Managed Office Space": {
    headline: (place) =>
      place ? `Managed Office Space in ${place}` : "Managed Office Space",
    priceSegment: (price) => (price ? `From ${price}` : "For Rent"),
    priceUnit: "desk",
    includePrice: true,
  },
  "Flexible Office Space": {
    headline: (place) =>
      place ? `Flexible Office Space in ${place}` : "Flexible Office Space",
    priceSegment: (price) =>
      price ? `No Lock-In from ${price}` : "No Lock-In",
    priceUnit: "monthly",
    includePrice: true,
  },
};

const DEFAULT_AREA_META_TITLE_TEMPLATE: AreaMetaTitleTemplate = {
  headline: (place) =>
    place ? `Office Space for Rent in ${place}` : "Office Space for Rent",
  priceSegment: (price) => (price ? `From ${price}` : "For Rent"),
  priceUnit: "desk",
  includePrice: true,
};

/** Maps API / filter labels to `AREA_PAGE_META_TITLE_BY_SPACE_TYPE` keys. */
const AREA_PAGE_SPACE_TYPE_TEMPLATE_ALIASES: Record<string, string> = {
  "sublease office": "Sublet Office",
  "sublet office": "Sublet Office",
  "sublet office space": "Sublet Office",
  "managed space": "Managed Office Space",
  "managed office": "Managed Office Space",
  "coworking desk": "Coworking Space",
  "enterprise suite": "Enterprise Office",
  "flexible office": "Flexible Office Space",
};

export function normalizeSpaceTypeForAreaPageTemplate(
  spaceType?: string,
): string | undefined {
  if (!spaceType) return undefined;
  const trimmed = sanitize(spaceType);
  const directKey = Object.keys(AREA_PAGE_META_TITLE_BY_SPACE_TYPE).find(
    (k) => k.toLowerCase() === trimmed.toLowerCase(),
  );
  if (directKey) return directKey;
  return AREA_PAGE_SPACE_TYPE_TEMPLATE_ALIASES[trimmed.toLowerCase()];
}

/** Whether the listing H1 should use generic "Office Space" instead of the area payload type. */
export function shouldUseGenericAreaPageHeadline(options: {
  selectedOfficeTypes?: string[];
  firstPageLoad?: string | boolean | null;
  workspaceType?: string | null;
  hasLocationQueryParams?: boolean;
}): boolean {
  const selected = (options.selectedOfficeTypes || []).filter(
    (t) => t && t !== "All",
  );
  if (selected.length > 0) return false;

  const isFirstPageLoadFalse =
    options.firstPageLoad === false || options.firstPageLoad === "false";
  if (isFirstPageLoadFalse) return true;

  const workspaceType = options.workspaceType?.trim();
  if (workspaceType === "All") return true;

  return Boolean(options.hasLocationQueryParams);
}

/** URL-driven browse context for area listing headlines (overrides API query defaults). */
export function getAreaHeadlineBrowseContext(
  searchParams: URLSearchParams,
  parsedQuery?: Record<string, string | undefined>,
) {
  const firstPageLoadFromUrl = searchParams.get("firstPageLoad");
  const workspaceTypeFromUrl = searchParams.get("workspaceType");

  return {
    firstPageLoad: firstPageLoadFromUrl ?? parsedQuery?.firstPageLoad,
    workspaceType:
      workspaceTypeFromUrl ??
      (firstPageLoadFromUrl === "false" ? "All" : parsedQuery?.workspaceType),
    hasLocationQueryParams: Boolean(
      searchParams.get("country") ||
      searchParams.get("state") ||
      searchParams.get("city") ||
      searchParams.get("scope") === "country",
    ),
  };
}

/** Space type for listing H1 / meta headlines from URL filter or area summary. */
export function resolveAreaPageHeadlineSpaceType(options: {
  selectedOfficeTypes?: string[];
  areaSpaceTypeFormula?: string;
  areaSpaceType?: string;
  officeTypeFilterTouched?: boolean;
  workspaceType?: string | null;
  firstPageLoad?: string | boolean | null;
  hasLocationQueryParams?: boolean;
}): string | undefined {
  if (options.officeTypeFilterTouched) {
    const wt = options.workspaceType?.trim();
    if (!wt || wt === "All") return undefined;
  }

  const normalized = (options.selectedOfficeTypes || []).filter(
    (t) => t && t !== "All",
  );

  if (normalized.length === 1) {
    const label = OFFICE_TYPES.find((t) => t.value === normalized[0])?.label;
    if (label) return normalizeSpaceTypeForAreaPageTemplate(label);
  }

  if (normalized.length > 1) {
    return undefined;
  }

  // None selected — generic heading when the user has changed the office-type filter.
  if (options.officeTypeFilterTouched) {
    const wt = options.workspaceType?.trim();
    if (!wt || wt === "All") return undefined;
    const parts = wt
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 1) {
      const label = OFFICE_TYPES.find((t) => t.value === parts[0])?.label;
      if (label) return normalizeSpaceTypeForAreaPageTemplate(label);
    }
    return undefined;
  }

  if (
    shouldUseGenericAreaPageHeadline({
      selectedOfficeTypes: options.selectedOfficeTypes,
      firstPageLoad: options.firstPageLoad,
      workspaceType: options.workspaceType,
      hasLocationQueryParams: options.hasLocationQueryParams,
    })
  ) {
    return undefined;
  }

  const raw = options.areaSpaceTypeFormula || options.areaSpaceType;
  return raw ? normalizeSpaceTypeForAreaPageTemplate(sanitize(raw)) : undefined;
}

export type AreaPageListingHeadlineInput = {
  spaceTypeFormula?: string;
  areaSummary?: AreaSummary;
  paramCity?: string;
  paramState?: string;
  paramCountry?: string;
  areaCity?: string;
  areaState?: string;
  areaCountry?: string;
  routeCountry?: string;
  hasAnyLocationParams?: boolean;
  /** When set, skips route/area place resolution (e.g. location geosearch last-two URL segments). */
  placeOverride?: string;
  /** Free-text `?q=` address shown in quotes before the place. */
  addressQuery?: string;
};

/** Listing page H1 — same phrasing as meta title headlines (no price segment). */
export function buildAreaPageListingHeadline(
  input: AreaPageListingHeadlineInput,
): string {
  const normalized = normalizeSpaceTypeForAreaPageTemplate(
    input.spaceTypeFormula,
  );
  const template = getAreaMetaTitleTemplate(normalized);
  const place =
    input.placeOverride?.trim() ||
    resolveAreaListingPlaceForHeadline({
      paramCity: input.paramCity,
      paramState: input.paramState,
      paramCountry: input.paramCountry,
      areaCity:
        input.areaCity ||
        input.areaSummary?.city ||
        input.areaSummary?.suburb,
      areaState: input.areaState || input.areaSummary?.state,
      areaCountry: input.areaCountry || input.areaSummary?.country,
      routeCountry: input.routeCountry,
      hasAnyLocationParams: Boolean(input.hasAnyLocationParams),
    }) ||
    (input.areaSummary ? getAreaPlaceForMetaTitle(input.areaSummary) : "");

  const addressQuery = input.addressQuery?.trim();
  const placeWithQuery =
    addressQuery && place
      ? `"${addressQuery}" ${place}`
      : addressQuery
        ? `"${addressQuery}"`
        : place;

  return template.headline(placeWithQuery || "");
}

/** Plural label for "N {label} Available in {place}" — aligned with H1 space-type rules. */
const AREA_PAGE_LISTING_AVAILABLE_LABEL: Record<string, string> = {
  "Serviced Office": "Serviced Offices",
  "Private Office": "Private Offices",
  "Coworking Space": "Coworking Spaces",
  "Sublet Office": "Sublease Office Spaces",
  "Virtual Office": "Virtual Offices",
  "Enterprise Office": "Enterprise Office Spaces",
  "Shared Office": "Shared Office Spaces",
  "Managed Office Space": "Managed Office Spaces",
  "Flexible Office Space": "Flexible Office Spaces",
};

function getAreaListingAvailableTypeLabel(spaceTypeFormula?: string): string {
  const normalized = normalizeSpaceTypeForAreaPageTemplate(spaceTypeFormula);
  if (!normalized) return "Office Spaces";
  return AREA_PAGE_LISTING_AVAILABLE_LABEL[normalized] ?? "Office Spaces";
}

/** e.g. "Serviced offices" or generic "office spaces" for pagination result text. */
export function formatAreaPageResultSpaceTypeLabel(
  spaceTypeFormula?: string,
): string {
  if (!spaceTypeFormula) return "Office Spaces";
  const label = getAreaListingAvailableTypeLabel(spaceTypeFormula);
  const words = label.split(" ").filter(Boolean);
  if (words.length === 0) return "office spaces";
  return [words[0], ...words.slice(1).map((w) => w.toLowerCase())].join(" ");
}

export type AreaPageListingResultTextInput = {
  start: number;
  end: number;
  total: number;
  spaceTypeFormula?: string;
  areaSummary?: AreaSummary;
  paramCity?: string;
  paramState?: string;
  paramCountry?: string;
  areaCity?: string;
  areaState?: string;
  areaCountry?: string;
  routeCountry?: string;
  hasAnyLocationParams?: boolean;
};

/** e.g. "Showing 1 - 30 of 52 Serviced offices in Perth, WA" */
export function buildAreaPageListingResultText(
  input: AreaPageListingResultTextInput,
): string {
  const typePhrase = formatAreaPageResultSpaceTypeLabel(input.spaceTypeFormula);
  const place = input.areaSummary
    ? resolveAreaListingPlaceForSubheadline({
      paramCity: input.paramCity,
      paramState: input.paramState,
      paramCountry: input.paramCountry,
      areaCity:
        input.areaCity ||
        input.areaSummary.city ||
        input.areaSummary.suburb,
      areaState: input.areaState || input.areaSummary.state,
      areaCountry: input.areaCountry || input.areaSummary.country,
      routeCountry: input.routeCountry,
      hasAnyLocationParams: Boolean(input.hasAnyLocationParams),
    }) || getAreaPlaceForMetaTitle(input.areaSummary)
    : "";
  const placeSegment = place ? ` in ${place}` : "";
  return `Showing ${input.start} - ${input.end} of ${input.total.toLocaleString()} ${typePhrase}${placeSegment}`;
}

export type AreaPageListingSubheadlineInput = {
  areaSummary?: AreaSummary;
  totalProperties: number;
  listings: PropertyListing[];
  fallbackListings: PropertyListing[];
  regionCurrency?: string;
  paramCity?: string;
  paramState?: string;
  paramCountry?: string;
  areaCity?: string;
  areaState?: string;
  areaCountry?: string;
  routeCountry?: string;
  hasAnyLocationParams?: boolean;
  selectedOfficeTypes?: string[];
  areaSpaceTypeFormula?: string;
  areaSpaceType?: string;
  officeTypeFilterTouched?: boolean;
  workspaceType?: string | null;
  firstPageLoad?: string | boolean | null;
  hasLocationQueryParams?: boolean;
  /** Raw place label when route/query location cannot be resolved (geo-search). */
  placeOverride?: string;
  /** Map legend tier starting prices from the onlyPins response. */
  globalPriceTier?: GlobalPriceTier | null;
  /** Map pins — used to derive starting price when API tiers are missing. */
  mapPins?: unknown[] | null;
};

/**
 * e.g. "32 Office Spaces Available in Perth, WA | Starting from A$167 per desk / mo"
 * Uses the same workspaceType / filter logic as the listing H1 and meta descriptions.
 */
export function buildAreaPageListingSubheadline(
  input: AreaPageListingSubheadlineInput,
): string | null {
  const place =
    input.placeOverride?.trim() ||
    resolveAreaListingPlaceForSubheadline({
      paramCity: input.paramCity,
      paramState: input.paramState,
      paramCountry: input.paramCountry,
      areaCity:
        input.areaCity ||
        input.areaSummary?.city ||
        input.areaSummary?.suburb,
      areaState: input.areaState || input.areaSummary?.state,
      areaCountry: input.areaCountry || input.areaSummary?.country,
      routeCountry: input.routeCountry,
      hasAnyLocationParams: Boolean(input.hasAnyLocationParams),
    }) ||
    (input.areaSummary ? getAreaPlaceForMetaTitle(input.areaSummary) : "");

  if (!place) return null;

  const listingCount =
    (input.listings?.length ?? 0) + (input.fallbackListings?.length ?? 0);
  const total = Math.max(input.totalProperties, listingCount);
  if (total <= 0) return null;

  const spaceType = resolveAreaPageHeadlineSpaceType({
    selectedOfficeTypes: input.selectedOfficeTypes,
    areaSpaceTypeFormula:
      input.areaSpaceTypeFormula ?? input.areaSummary?.spaceTypeFormula,
    areaSpaceType: input.areaSpaceType ?? input.areaSummary?.spaceType,
    officeTypeFilterTouched: input.officeTypeFilterTouched,
    workspaceType: input.workspaceType,
    firstPageLoad: input.firstPageLoad,
    hasLocationQueryParams: input.hasLocationQueryParams,
  });
  const typeLabel = getAreaListingAvailableTypeLabel(spaceType);
  const template = getAreaMetaTitleTemplate(spaceType);
  const availablePart = `${total.toLocaleString()} ${typeLabel} Available in ${place}`;

  if (!template.includePrice) return availablePart;

  const regionCurrency =
    input.regionCurrency ||
    input.areaSummary?.currencyIsoCode ||
    "AUD";

  // Prefer the same source as the map legend (globalPriceTier → tiered pins).
  const minPrice =
    getMapTierStartingPrice(
      input.globalPriceTier,
      input.mapPins,
      regionCurrency,
    ) ||
    getMinPerDeskPriceForAreaTitle(
      input.areaSummary,
      input.listings,
      input.fallbackListings,
      regionCurrency,
    );
  if (!minPrice) return availablePart;

  const amountStr = formatPriceAmountForMetaTitle(
    minPrice.amount,
    minPrice.currencyIsoCode,
    input.regionCurrency,
  );
  if (!amountStr) return availablePart;

  const unitPhrase =
    template.priceUnit === "desk" ? "per desk / month" : "per month";

  return `${availablePart} | Starting from ${amountStr} ${unitPhrase}`;
}

function getAreaMetaTitleTemplate(
  spaceTypeFormula?: string,
): AreaMetaTitleTemplate {
  const normalized = normalizeSpaceTypeForAreaPageTemplate(spaceTypeFormula);
  if (!normalized) return DEFAULT_AREA_META_TITLE_TEMPLATE;
  const key = Object.keys(AREA_PAGE_META_TITLE_BY_SPACE_TYPE).find(
    (k) => k.toLowerCase() === normalized.toLowerCase(),
  );
  return key
    ? AREA_PAGE_META_TITLE_BY_SPACE_TYPE[key]
    : DEFAULT_AREA_META_TITLE_TEMPLATE;
}

function formatPriceAmountForMetaTitle(
  amount: number,
  fromCurrency: string,
  targetCurrency?: string,
): string | undefined {
  // Convert from the listing/area source currency to the region currency, mirroring
  // PGridCard's display logic: convertCurrency(amount, currencyIsoCode, regionCurrency).
  const target = targetCurrency || fromCurrency;
  const formatted = convertCurrency(amount, fromCurrency, target);
  return formatted || undefined;
}

function formatPriceWithUnitForMetaTitle(
  amount: number,
  fromCurrency: string,
  unit: AreaMetaTitlePriceUnit,
  targetCurrency?: string,
): string | undefined {
  const amountStr = formatPriceAmountForMetaTitle(
    amount,
    fromCurrency,
    targetCurrency,
  );
  if (!amountStr) return undefined;
  return unit === "desk" ? `${amountStr}/desk/mth` : `${amountStr}/mth`;
}

function getMinPerDeskPriceFromRecord(
  record: {
    avgMonthlyCostPerWorkstation?: number;
    costPerWorkstation?: number;
    deskMonthlyPrice?: number;
  } | null | undefined,
): number | undefined {
  return (
    toPositiveNumber(record?.avgMonthlyCostPerWorkstation) ??
    toPositiveNumber(record?.costPerWorkstation) ??
    toPositiveNumber(record?.deskMonthlyPrice)
  );
}

/** Per-desk starting price only — matches map legend / card "per desk / mo". */
function getMinPerDeskPriceForAreaTitle(
  areaSummary: AreaSummary | undefined,
  listings: PropertyListing[],
  fallbackListings: PropertyListing[],
  regionCurrency?: string,
): { amount: number; currencyIsoCode: string } | undefined {
  const defaultCurrency =
    areaSummary?.currencyIsoCode || regionCurrency || "AUD";
  const listingSource = listings.length > 0 ? listings : fallbackListings;

  let min: number | undefined;
  let currencyIsoCode = defaultCurrency;

  for (const listing of listingSource) {
    const nested = (listing.propertyListings ?? [])
      .map((unit) => getMinPerDeskPriceFromRecord(unit))
      .filter((price): price is number => price != null);
    const propertyLevel = getMinPerDeskPriceFromRecord(listing);
    const candidates = propertyLevel != null ? [...nested, propertyLevel] : nested;
    if (candidates.length === 0) continue;
    const listingMin = Math.min(...candidates);
    if (min === undefined || listingMin < min) {
      min = listingMin;
      currencyIsoCode = listing.currencyIsoCode || currencyIsoCode;
    }
  }

  if (min != null) return { amount: min, currencyIsoCode };

  const fromSummary = getMinPerDeskPriceFromRecord(areaSummary);
  if (fromSummary == null) return undefined;
  return { amount: fromSummary, currencyIsoCode: defaultCurrency };
}

function getMinMonthlyPriceForAreaTitle(
  areaSummary: AreaSummary | undefined,
  listings: PropertyListing[],
  fallbackListings: PropertyListing[],
  regionCurrency?: string,
): { amount: number; currencyIsoCode: string } | undefined {
  const defaultCurrency =
    areaSummary?.currencyIsoCode || regionCurrency || "AUD";
  const listingSource =
    listings.length > 0 ? listings : fallbackListings;
  const fromListings = getMinAvgMonthlyCostPerWorkstation(
    listingSource,
    defaultCurrency,
  );
  if (fromListings) return fromListings;

  if (!areaSummary) return undefined;

  const fromSummary =
    toPositiveNumber(areaSummary.deskMonthlyPrice) ??
    toPositiveNumber(areaSummary.avgMonthlyCostPerWorkstation);
  if (fromSummary == null) return undefined;
  return { amount: fromSummary, currencyIsoCode: defaultCurrency };
}

/**
 * e.g. "Serviced Offices in Perth, WA | All-Inclusive from $163/desk/mth"
 */
function buildAreaPageMetaTitle(
  areaSummary: AreaSummary,
  listings: PropertyListing[],
  fallbackListings: PropertyListing[],
  regionCurrency?: string,
  options?: {
    /** When set (including `undefined`), overrides area-summary space type for filter-aware meta. */
    spaceTypeFormula?: string;
    place?: string;
  },
  globalPriceTier?: GlobalPriceTier | null,
  mapPins?: unknown[] | null,
): string {
  const spaceTypeFormula =
    options && "spaceTypeFormula" in options
      ? options.spaceTypeFormula
      : resolveAreaSpaceTypeFormula(areaSummary);
  const template = getAreaMetaTitleTemplate(spaceTypeFormula);
  const place = options?.place || getAreaPlaceForMetaTitle(areaSummary);
  const headline = template.headline(place);

  let priceWithUnit: string | undefined;
  if (template.includePrice) {
    const minPrice =
      getMapTierStartingPrice(
        globalPriceTier,
        mapPins,
        regionCurrency || areaSummary.currencyIsoCode || "AUD",
      ) ||
      getMinPerDeskPriceForAreaTitle(
        areaSummary,
        listings,
        fallbackListings,
        regionCurrency,
      ) ||
      getMinMonthlyPriceForAreaTitle(
        areaSummary,
        listings,
        fallbackListings,
        regionCurrency,
      );
    if (minPrice) {
      priceWithUnit = formatPriceWithUnitForMetaTitle(
        minPrice.amount,
        minPrice.currencyIsoCode,
        template.priceUnit,
        regionCurrency,
      );
    }
  }

  const suffix = template.priceSegment(priceWithUnit);
  return suffix ? `${headline} | ${suffix}` : headline;
}


function getMinAvgMonthlyCostPerWorkstation(
  listings: PropertyListing[],
  defaultCurrency: string,
): { amount: number; currencyIsoCode: string } | undefined {
  let min: number | undefined;
  let currencyIsoCode = defaultCurrency;

  for (const listing of listings) {
    const prices = getPropertyMonthlyPrices(listing);
    if (prices.length === 0) continue;
    const listingMin = Math.min(...prices);
    if (min === undefined || listingMin < min) {
      min = listingMin;
      currencyIsoCode = listing.currencyIsoCode || currencyIsoCode;
    }
  }

  if (min === undefined) return undefined;
  return { amount: min, currencyIsoCode };
}

interface AreaMetaDescriptionContext {
  place: string;
  locality: string;
  /** e.g. "160+" */
  countPlus: string;
  /** e.g. "160+ " when count is known */
  countPrefix: string;
  priceDesk?: string;
  priceMonthly?: string;
  /** e.g. " for 1–50+ people" from listing desk range */
  peopleCapacityPhrase?: string;
}

function inPlaceSegment(place: string): string {
  return place ? ` in ${place}` : "";
}

type AreaMetaDescriptionBuilder = (ctx: AreaMetaDescriptionContext) => string;

const DEFAULT_AREA_META_DESCRIPTION: AreaMetaDescriptionBuilder = (ctx) => {
  const count = ctx.countPlus.replace(/\+$/, "") || "";
  const countPart = count ? `${count} ` : "";
  const pricePart = ctx.priceDesk
    ? ` from ${ctx.priceDesk.replace(/\/desk\/mo$/, "").replace(/\/mo$/, "")} per desk/month`
    : "";
  return `Compare ${countPart}office spaces${inPlaceSegment(ctx.place)}, including serviced, private and coworking offices${pricePart}. Book a free tour.`;
};

/** Short page-1 description for a specific space type, e.g. coworking / serviced office. */
function buildSpaceTypeCompareDescription(
  typePhrase: string,
): AreaMetaDescriptionBuilder {
  return (ctx) => {
    const count = ctx.countPlus.replace(/\+$/, "") || "";
    const countPart = count ? `${count} ` : "";
    const pricePart = ctx.priceDesk
      ? ` from ${ctx.priceDesk.replace(/\/desk\/mo$/, "").replace(/\/mo$/, "")} per desk/month`
      : ctx.priceMonthly
        ? ` from ${ctx.priceMonthly.replace(/\/mo$/, "")} per desk/month`
        : "";
    return `Compare ${countPart}${typePhrase}${inPlaceSegment(ctx.place)}${pricePart}. Book a free tour.`;
  };
}

function getAreaMetaDescriptionBuilder(
  spaceTypeFormula?: string,
): AreaMetaDescriptionBuilder {
  const normalized = normalizeSpaceTypeForAreaPageTemplate(spaceTypeFormula);
  if (!normalized) return DEFAULT_AREA_META_DESCRIPTION;
  const typePhrase = formatAreaPageResultSpaceTypeLabel(normalized).toLowerCase();
  return buildSpaceTypeCompareDescription(typePhrase);
}

function buildAreaMetaDescriptionContext(
  areaSummary: AreaSummary,
  totalProperties: number,
  listings: PropertyListing[],
  fallbackListings: PropertyListing[],
  regionCurrency?: string,
  globalPriceTier?: GlobalPriceTier | null,
  mapPins?: unknown[] | null,
): AreaMetaDescriptionContext {
  const place = getAreaPlaceForMetaTitle(areaSummary);
  const locality = getAreaLocalityName(areaSummary);
  const countPlus =
    totalProperties > 0 ? `${totalProperties.toLocaleString()}+` : "";
  const countPrefix = countPlus ? `${countPlus} ` : "";

  const listingSource =
    listings.length > 0 ? listings : fallbackListings;

  const minPrice =
    getMapTierStartingPrice(
      globalPriceTier,
      mapPins,
      regionCurrency || areaSummary.currencyIsoCode || "AUD",
    ) ||
    getMinPerDeskPriceForAreaTitle(
      areaSummary,
      listings,
      fallbackListings,
      regionCurrency,
    ) ||
    getMinMonthlyPriceForAreaTitle(
      areaSummary,
      listings,
      fallbackListings,
      regionCurrency,
    );

  let priceDesk: string | undefined;
  let priceMonthly: string | undefined;
  if (minPrice) {
    priceDesk = formatPriceWithUnitForMetaTitle(
      minPrice.amount,
      minPrice.currencyIsoCode,
      "desk",
      regionCurrency,
    );
    priceMonthly = formatPriceWithUnitForMetaTitle(
      minPrice.amount,
      minPrice.currencyIsoCode,
      "monthly",
      regionCurrency,
    );
  }

  const deskCap = getDeskCapacityRangeFromListingPayload(listingSource);
  const peopleCapacityPhrase = deskCap
    ? formatMetaDescriptionPeopleCapacity(deskCap)
    : undefined;

  return {
    place,
    locality,
    countPlus,
    countPrefix,
    priceDesk,
    priceMonthly,
    peopleCapacityPhrase,
  };
}

/**
 * e.g. "Browse 160+ serviced offices in Perth, WA from $163/desk/mth. All-inclusive..."
 */
function buildAreaPageMetaDescription(
  areaSummary: AreaSummary,
  totalProperties: number,
  listings: PropertyListing[],
  fallbackListings: PropertyListing[],
  regionCurrency?: string,
  options?: {
    spaceTypeFormula?: string;
    place?: string;
  },
  globalPriceTier?: GlobalPriceTier | null,
  mapPins?: unknown[] | null,
): string {
  const spaceTypeFormula =
    options && "spaceTypeFormula" in options
      ? options.spaceTypeFormula
      : resolveAreaSpaceTypeFormula(areaSummary);
  const builder = getAreaMetaDescriptionBuilder(spaceTypeFormula);
  const ctx = buildAreaMetaDescriptionContext(
    areaSummary,
    totalProperties,
    listings,
    fallbackListings,
    regionCurrency,
    globalPriceTier,
    mapPins,
  );
  if (options?.place) {
    ctx.place = options.place;
  }
  return builder(ctx);
}

/**
 * Shorter page-2+ meta description (keeps under ~160 chars):
 * "View office spaces 31–60 of 449 in City of London. Compare serviced, private and coworking offices from £129 per desk/month."
 */
function buildAreaPagePaginatedMetaDescription(input: {
  start: number;
  end: number;
  total: number;
  areaSummary: AreaSummary;
  listings: PropertyListing[];
  fallbackListings: PropertyListing[];
  regionCurrency?: string;
  spaceTypeFormula?: string;
  place?: string;
  globalPriceTier?: GlobalPriceTier | null;
  mapPins?: unknown[] | null;
}): string {
  const place = input.place || getAreaPlaceForMetaTitle(input.areaSummary);
  const placeSeg = place ? ` in ${place}` : "";
  const spaceTypeFormula =
    "spaceTypeFormula" in input
      ? input.spaceTypeFormula
      : resolveAreaSpaceTypeFormula(input.areaSummary);
  const typePhrase = formatAreaPageResultSpaceTypeLabel(
    spaceTypeFormula,
  ).toLowerCase();

  const minPrice =
    getMapTierStartingPrice(
      input.globalPriceTier,
      input.mapPins,
      input.regionCurrency || input.areaSummary.currencyIsoCode || "AUD",
    ) ||
    getMinPerDeskPriceForAreaTitle(
      input.areaSummary,
      input.listings,
      input.fallbackListings,
      input.regionCurrency,
    ) ||
    getMinMonthlyPriceForAreaTitle(
      input.areaSummary,
      input.listings,
      input.fallbackListings,
      input.regionCurrency,
    );
  const amountStr = minPrice
    ? formatPriceAmountForMetaTitle(
        minPrice.amount,
        minPrice.currencyIsoCode,
        input.regionCurrency,
      )
    : undefined;
  const priceSeg = amountStr ? ` from ${amountStr} per desk/month` : "";

  const compareSentence = spaceTypeFormula
    ? `Compare ${typePhrase}${priceSeg}.`
    : `Compare serviced, private and coworking offices${priceSeg}.`;

  return `View ${typePhrase} ${input.start}–${input.end} of ${input.total.toLocaleString()}${placeSeg}. ${compareSentence} Book a free tour.`;
}

/**
 * Resolve space type for document title/description from URL filters —
 * same rules as the on-page listing H1.
 */
function resolveAreaMetaSpaceTypeFromSearch(
  search: string | undefined,
  areaSummary: AreaSummary,
): string | undefined {
  const params = new URLSearchParams(
    search?.startsWith("?") ? search.slice(1) : search ?? "",
  );
  const browse = getAreaHeadlineBrowseContext(params);
  const workspaceType = params.get("workspaceType") ?? browse.workspaceType;
  const officeTypeFilterTouched =
    params.get("officeTypeFilterTouched") === "1";
  const selectedOfficeTypes =
    workspaceType && workspaceType !== "All"
      ? workspaceType
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

  return resolveAreaPageHeadlineSpaceType({
    selectedOfficeTypes,
    areaSpaceTypeFormula: areaSummary.spaceTypeFormula,
    areaSpaceType: areaSummary.spaceType,
    officeTypeFilterTouched,
    workspaceType,
    firstPageLoad: browse.firstPageLoad,
    hasLocationQueryParams: browse.hasLocationQueryParams,
  });
}

/** Prefer URL location filters for place label when present (breadcrumb / drawer). */
function resolveAreaMetaPlaceFromSearch(
  search: string | undefined,
  areaSummary: AreaSummary,
): string {
  const params = new URLSearchParams(
    search?.startsWith("?") ? search.slice(1) : search ?? "",
  );
  const paramCity = params.get("city") || "";
  const paramState = params.get("state") || "";
  const paramCountry = (params.get("country") || "").toLowerCase();
  const hasAnyLocationParams = Boolean(
    paramCity || paramState || paramCountry,
  );

  return (
    resolveAreaListingPlaceForHeadline({
      paramCity,
      paramState,
      paramCountry,
      areaCity: areaSummary.city || areaSummary.suburb,
      areaState: areaSummary.state,
      areaCountry: areaSummary.country,
      hasAnyLocationParams,
    }) || getAreaPlaceForMetaTitle(areaSummary)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────────────────────

/** Default page size for area / location listing routes (search uses 40). */
const AREA_LISTING_META_PAGE_SIZE = 30;

function parseListingPageFromSearch(search?: string): number {
  if (!search) return 1;
  const raw = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  ).get("page");
  const page = raw ? parseInt(raw, 10) : 1;
  return Number.isFinite(page) && page > 1 ? page : 1;
}

export const areaMeta = ({
  data,
  path,
  search,
  listingsPerPage = AREA_LISTING_META_PAGE_SIZE,
  schemaItemMode = "property",
}: {
  data: {
    country?: CountryMetaData;
    regionData?: RegionData;
    areaSummary?: AreaSummary;
    data?: GeoSearchMetaData;
    /** Map legend tier prices from onlyPins — preferred for meta "from" prices. */
    globalPriceTier?: GlobalPriceTier | null;
    mapPins?: unknown[] | null;
  };
  path: string;
  /** Browser `location.search` — used for paginated canonical / title / description. */
  search?: string;
  /** Page size used for paginated meta description item ranges. */
  listingsPerPage?: number;
  /** Geo-search pages pass flat listing units; area pages pass property aggregates. */
  schemaItemMode?: SchemaItemMode;
}) => {
  const regionConfig = data?.regionData;
  const areaSummary: AreaSummary = data?.areaSummary ?? {};
  const geoSearch = data?.data;
  const properties: PropertyListing[] = geoSearch?.data ?? [];
  const autoReCalibratedListings: PropertyListing[] =
    geoSearch?.autoReCalibratedData ?? [];
  const totalProperties =
    geoSearch?.totalProperties ?? geoSearch?.totalListings ?? 0;
  const regionCurrency = regionConfig?.currencyIsoCode ?? regionConfig?.currency;
  const page = parseListingPageFromSearch(search);
  const pageSize =
    listingsPerPage > 0 ? listingsPerPage : AREA_LISTING_META_PAGE_SIZE;
  const globalPriceTier = data?.globalPriceTier ?? null;
  const mapPins = data?.mapPins ?? null;

  // Match on-page H1: URL filters (workspace type / location) override area defaults.
  const effectiveSpaceType = resolveAreaMetaSpaceTypeFromSearch(
    search,
    areaSummary,
  );
  const effectivePlace = resolveAreaMetaPlaceFromSearch(search, areaSummary);
  const metaOptions = {
    spaceTypeFormula: effectiveSpaceType,
    place: effectivePlace,
  };

  const baseTitle =
    effectiveSpaceType ||
      areaSummary.city ||
      areaSummary.suburb ||
      areaSummary.spaceTypeFormula ||
      areaSummary.spaceType
      ? buildAreaPageMetaTitle(
          areaSummary,
          properties,
          autoReCalibratedListings,
          regionCurrency,
          metaOptions,
          globalPriceTier,
          mapPins,
        )
      : (areaSummary.metaTitle ??
        buildAreaPageMetaTitle(
          areaSummary,
          properties,
          autoReCalibratedListings,
          regionCurrency,
          metaOptions,
          globalPriceTier,
          mapPins,
        ));

  const title = page > 1 ? `${baseTitle} – Page ${page}` : baseTitle;

  const description =
    page > 1 && totalProperties > 0
      ? buildAreaPagePaginatedMetaDescription({
          start: (page - 1) * pageSize + 1,
          end: Math.min(page * pageSize, totalProperties),
          total: totalProperties,
          areaSummary,
          listings: properties,
          fallbackListings: autoReCalibratedListings,
          regionCurrency,
          globalPriceTier,
          mapPins,
          ...metaOptions,
        })
      : buildAreaPageMetaDescription(
          areaSummary,
          totalProperties,
          properties,
          autoReCalibratedListings,
          regionCurrency,
          metaOptions,
          globalPriceTier,
          mapPins,
        );

  const _baseUrl = regionConfig?.region
    ? DomainNameWithSubPathForLocale(regionConfig.region)
    : GetDomainName();

  const canonicalBase = GetDomainName();
  let pageUrl =
    canonicalBase.endsWith("/") && path === "/"
      ? canonicalBase.slice(0, -1)
      : canonicalBase + (path === "/" ? "" : path);

  // Self-referencing canonical: include ?page=N on paginated listing pages.
  if (page > 1) {
    pageUrl += `?page=${page}`;
  }

  const canonicalLink = [
    {
      tagName: "link",
      rel: "canonical",
      href: pageUrl,
    },
  ];

  const collectionPageSchema = buildCollectionPageSchema(
    areaSummary,
    properties,
    pageUrl,
    _baseUrl,
    canonicalBase,
    title,
    description,
    totalProperties,
    schemaItemMode,
  );

  const safeCollectionSchema = deepSanitizeSchema(collectionPageSchema);
  const breadcrumbSchema = buildAreaBreadcrumbSchema(
    areaSummary,
    pageUrl,
    canonicalBase,
  );

  const ldJson: { "script:ld+json": any }[] = [
    { "script:ld+json": safeCollectionSchema },
    ...(breadcrumbSchema
      ? [{ "script:ld+json": deepSanitizeSchema(breadcrumbSchema) }]
      : []),
  ];
  return [
    ...getMetaOgTags({
      title,
      description,
      imageUrl: areaSummary.imageUrl,
      canonicalUrl: pageUrl,
    }),
    ...canonicalLink,
    ...ldJson,
  ];
};
